/**
 * Multiplayer Manager
 * 
 * High-level integration service that orchestrates the complete multiplayer flow.
 * Manages UI components, services, and coordinates the end-to-end multiplayer experience.
 */

import { GameSession } from '../services/GameSession';
import { NetworkSync, resolveMultiplayerConfig } from '../services/NetworkSync';
import type { EventSubscription } from '../services/NetworkSync';
import { ShareCodeInput } from '../../ui/components/ShareCodeInput';
import { GameLobby } from '../../ui/components/GameLobby';

import { PlayerRole } from '../../multiplayer/types/NetworkTypes';
import type { EndTurnPayload } from '../../multiplayer/types/NetworkTypes';

// ==================== Backend URL Validation ====================

/**
 * Validates that the configured multiplayer backend is reachable and properly configured.
 * Used to catch configuration issues before attempting to create or join a session.
 */
function validateBackendConfiguration(): { valid: boolean; error?: string } {
  const config = resolveMultiplayerConfig();
  
  try {
    const url = new URL(config.serverUrl);
    
    // Warn or error if production-like domain but using development protocol
    const isProductionDomain = !url.hostname.includes('localhost') && !url.hostname.includes('127.0.0.1');
    const isInsecureProtocol = url.protocol === 'http:';
    
    if (isProductionDomain && isInsecureProtocol) {
      return {
        valid: false,
        error: 
          'Backend configuration error: Production domain requires HTTPS. ' +
          `Update VITE_MULTIPLAYER_SERVER_URL to use https://${url.hostname}`
      };
    }
    
    // Check for localhost usage in deployment contexts
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      const isDeployedToProduction = import.meta.env.MODE === 'production' && 
                                   document.location.protocol === 'https:';
      
      if (isDeployedToProduction) {
        return {
          valid: false,
          error: 
            'Multiplayer service unavailable: Backend is not deployed to production. ' +
            'Please contact support.'
        };
      }
    }
    
    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: `Invalid backend URL configuration: ${config.serverUrl}`
    };
  }
}

export interface MultiplayerManagerOptions {
  playerName?: string;
  autoReconnect?: boolean;
  onGameStart?: () => void;
  onError?: (error: string) => void;
  onSessionEnd?: () => void;
}

export interface SessionCreateOptions {
  displayName: string;
  maxPlayers?: number;
}

export interface SessionJoinOptions {
  shareCode: string;
  displayName: string;
}

export type MultiplayerState = 'disconnected' | 'connecting' | 'lobby' | 'creating' | 'joining' | 'in-game' | 'error';

export class MultiplayerManager {
  private static instance: MultiplayerManager | null = null;
  
  // Core services
  private networkSync: NetworkSync;
  private gameSession: GameSession;
  
  // UI components
  private shareCodeInput: ShareCodeInput | null = null;
  private gameLobby: GameLobby | null = null;
  
  // State management
  private currentState: MultiplayerState = 'disconnected';
  private options: MultiplayerManagerOptions;
  private lastError: string | null = null;
  private managerSubscriptions: EventSubscription[] = [];
  private lastEndTurnSentAt = 0;
  private readonly END_TURN_DEBOUNCE_MS = 250;
  
  // Configuration
  private defaultPlayerName: string = 'Player';

  constructor(options: MultiplayerManagerOptions = {}) {
    this.options = {
      playerName: 'Player',
      autoReconnect: true,
      ...options
    };
    
    this.defaultPlayerName = this.options.playerName || 'Player';
    
    // Initialize services
    this.networkSync = NetworkSync.getInstance();
    this.gameSession = new GameSession(this.networkSync);
    
    this.setupEventHandlers();
    
    console.log('[MultiplayerManager] Initialized');
  }

  // ==================== Singleton Pattern ====================

  public static getInstance(options?: MultiplayerManagerOptions): MultiplayerManager {
    if (!MultiplayerManager.instance) {
      MultiplayerManager.instance = new MultiplayerManager(options);
    } else if (options) {
      MultiplayerManager.instance.updateOptions(options);
    }
    return MultiplayerManager.instance;
  }

  public static resetInstance(): void {
    if (MultiplayerManager.instance) {
      MultiplayerManager.instance.cleanup();
      MultiplayerManager.instance = null;
    }
  }

  // ==================== Event Handlers Setup ====================

  private setupEventHandlers(): void {
    // Game session event handlers
    this.gameSession.setCallbacks({
      onSessionCreated: (data) => {
        console.log('[MultiplayerManager] Session created:', data.shareCode);
        this.setState('lobby');
        this.showLobby();
        this.hideShareCodeInput();
      },

      onSessionJoined: (data) => {
        console.log('[MultiplayerManager] Joined session:', data.sessionId);
        this.setState('lobby');
        this.showLobby();
        this.hideShareCodeInput();
      },

      onJoinError: (data) => {
        console.warn('[MultiplayerManager] Join error:', data.message);
        this.handleError(data.message);
      },

      onPlayerJoined: (data) => {
        console.log('[MultiplayerManager] New player joined:', data.player.displayName);
        if (this.gameLobby) {
          this.gameLobby.refresh();
        }
      },

      onPlayerLeft: (data) => {
        console.log('[MultiplayerManager] Player left:', data.displayName);
        if (this.gameLobby) {
          this.gameLobby.refresh();
        }
      },

      onGameStarted: (_data) => {
        console.log('[MultiplayerManager] Game started - transitioning to game');
        this.setState('in-game');
        this.hideLobby();
        
        if (this.options.onGameStart) {
          this.options.onGameStart();
        }
      },

      onSessionEnded: () => {
        console.log('[MultiplayerManager] Session ended');
        this.handleSessionEnd();
      },

      onConnectionLost: () => {
        console.warn('[MultiplayerManager] Connection lost');
        this.handleConnectionLoss();
      },

      onConnectionRestored: () => {
        console.log('[MultiplayerManager] Connection restored');
        this.handleConnectionRestored();
      },

      onRestoreFailed: (message: string) => {
        console.warn('[MultiplayerManager] Session restore failed:', message);
        this.handleRestoreFailed(message);
      },

      onGameAction: (actionType, actionData) => {
        console.log(`[MultiplayerManager] Game action received: ${actionType}`, actionData);
        
        // Forward game actions to the PoolScene if it has been set
        if (this.onGameAction) {
          console.log(`[MultiplayerManager] Forwarding to registered onGameAction handler`);
          this.onGameAction(actionType, actionData);
        } else {
          console.warn(`[MultiplayerManager] No onGameAction handler registered (PoolScene not ready?)`);
        }
      }
    });

    // Propagate authoritative board snapshot updates through manager-level handlers so
    // scenes that listen via MultiplayerManager receive the latest turn-handoff projection.
    this.managerSubscriptions.push(
      this.networkSync.on('game_state_update', (payload: any) => {
        if (this.onGameStateUpdate) {
          this.onGameStateUpdate(payload);
        }
      })
    );

    this.managerSubscriptions.push(
      this.networkSync.on('turn_change', (payload: any) => {
        this.lastEndTurnSentAt = 0;
        if (this.onTurnChange) {
          this.onTurnChange(payload);
        }
      })
    );
  }

  // ==================== Public Session Management ====================

  public async createSession(options?: SessionCreateOptions): Promise<void> {
    try {
      // Validate backend configuration before attempting to create session
      const configCheck = validateBackendConfiguration();
      if (!configCheck.valid) {
        this.handleError(configCheck.error!);
        return;
      }
      
      this.setState('creating');
      
      const displayName = options?.displayName || this.defaultPlayerName;
      const maxPlayers = options?.maxPlayers || 4;
      
      console.log('[MultiplayerManager] Creating session for:', displayName);
      
      await this.gameSession.createSession({
        displayName,
        maxPlayers
      });
      
      // Success handling is done in the callback
      
    } catch (error) {
      console.error('[MultiplayerManager] Failed to create session:', error);
      this.handleError(error instanceof Error ? error.message : 'Failed to create session');
    }
  }

  public showJoinDialog(displayName?: string): void {
    this.setState('joining');
    
    if (!this.shareCodeInput) {
      this.initializeShareCodeInput();
    }
    
    if (displayName) {
      this.shareCodeInput!.setDisplayName(displayName);
    }
    
    this.shareCodeInput!.show();
  }

  public async joinSession(options: SessionJoinOptions): Promise<void> {
    try {
      // Validate backend configuration before attempting to join session
      const configCheck = validateBackendConfiguration();
      if (!configCheck.valid) {
        this.handleError(configCheck.error!);
        return;
      }
      
      this.setState('joining');
      
      console.log('[MultiplayerManager] Joining session:', options.shareCode);
      
      await this.gameSession.joinSession(options);
      
      // Success handling is done in the callback
      
    } catch (error) {
      console.error('[MultiplayerManager] Failed to join session:', error);
      this.handleError(error instanceof Error ? error.message : 'Failed to join session');
    }
  }

  public leaveSession(): void {
    console.log('[MultiplayerManager] Leaving current session');
    
    if (this.gameSession.isInSession()) {
      this.gameSession.leaveSession();
    }
    
    this.hideLobby();
    this.hideShareCodeInput();
    this.setState('disconnected');
  }

  public startGame(): void {
    if (!this.gameSession.isInSession()) {
      this.handleError('Not in a session');
      return;
    }

    const sessionState = this.gameSession.getSessionState();
    if (!sessionState.isCreator) {
      this.handleError('Only the session creator can start the game');
      return;
    }

    const playerCount = sessionState.players.filter(p => p.role === PlayerRole.PLAYER).length;
    if (playerCount < 2) {
      this.handleError('Need at least 2 players to start');
      return;
    }

    console.log('[MultiplayerManager] Starting game');
    // Send start_game event to backend
    this.networkSync.startGame();
  }

  // ==================== UI Component Management ====================

  private initializeShareCodeInput(): void {
    if (this.shareCodeInput) {
      return;
    }

    this.shareCodeInput = new ShareCodeInput({
      displayName: this.defaultPlayerName,
      onSuccess: (shareCode) => {
        console.log('[MultiplayerManager] Share code input success:', shareCode);
        // The join process is handled by the GameSession callbacks
      },
      onError: (message) => {
        console.warn('[MultiplayerManager] Share code input error:', message);
        this.handleError(message);
      },
      onCancel: () => {
        console.log('[MultiplayerManager] Share code input cancelled');
        this.setState('disconnected');
        this.hideShareCodeInput();
      }
    });
  }

  private initializeGameLobby(): void {
    if (this.gameLobby) {
      return;
    }

    this.gameLobby = new GameLobby(this.gameSession, {
      onStartGame: () => {
        this.startGame();
      },
      onLeaveSession: () => {
        this.leaveSession();
      },
      onInvitePlayer: (shareCode) => {
        this.handleInvitePlayer(shareCode);
      },
      onPlayerAction: (playerId, action) => {
        this.handlePlayerAction(playerId, action);
      }
    });
  }

  private showLobby(): void {
    if (!this.gameLobby) {
      this.initializeGameLobby();
    }
    
    this.gameLobby!.show();
  }

  private hideLobby(): void {
    if (this.gameLobby && this.gameLobby.isShown()) {
      this.gameLobby.hide();
    }
  }

  private hideShareCodeInput(): void {
    if (this.shareCodeInput && this.shareCodeInput.isShown()) {
      this.shareCodeInput.hide();
    }
  }

  // ==================== State Management ====================

  private setState(newState: MultiplayerState): void {
    const oldState = this.currentState;
    this.currentState = newState;
    
    console.log(`[MultiplayerManager] State change: ${oldState} → ${newState}`);
    
    // Clear error state when transitioning from error
    if (oldState === 'error' && newState !== 'error') {
      this.lastError = null;
    }
  }

  // ==================== Event Handlers ====================

  private handleError(message: string): void {
    console.error('[MultiplayerManager] Error:', message);
    this.lastError = message;
    this.setState('error');
    
    if (this.options.onError) {
      this.options.onError(message);
    }
  }

  private handleSessionEnd(): void {
    console.log('[MultiplayerManager] Session ended, cleaning up');
    this.hideLobby();
    this.hideShareCodeInput();
    this.setState('disconnected');
    
    if (this.options.onSessionEnd) {
      this.options.onSessionEnd();
    }
  }

  private handleConnectionLoss(): void {
    if (this.currentState === 'lobby' || this.currentState === 'in-game') {
      // Show reconnecting state in UI
      if (this.gameLobby && this.gameLobby.isShown()) {
        // The GameLobby component handles connection status display
      }
    }
  }

  private handleConnectionRestored(): void {
    // Refresh UI state after reconnection
    if (this.gameLobby && this.gameLobby.isShown()) {
      this.gameLobby.refresh();
    }
  }

  private handleRestoreFailed(message: string): void {
    if (this.onRestoreFailed) {
      this.onRestoreFailed(message);
    }
  }

  private handleInvitePlayer(shareCode: string): void {
    // Copy share code to clipboard and show notification
    navigator.clipboard.writeText(shareCode).then(() => {
      console.log('[MultiplayerManager] Share code copied for invite');
    }).catch((err) => {
      console.warn('[MultiplayerManager] Failed to copy share code:', err);
    });
    
    // Could also trigger platform-specific sharing here
  }

  private handlePlayerAction(playerId: string, action: 'kick' | 'promote'): void {
    console.log(`[MultiplayerManager] Player action: ${action} for ${playerId}`);
    
    // For now, just log the action
    // In a full implementation, this would send the appropriate network events
    
    switch (action) {
      case 'kick':
        // Would send kick_player event to backend
        console.log('[MultiplayerManager] Kicking player not implemented yet');
        break;
      case 'promote':
        // Would send promote_player event to backend  
        console.log('[MultiplayerManager] Promoting player not implemented yet');
        break;
    }
  }

  // ==================== Auto-reconnection ====================

  // ==================== Public State Access ====================

  public getState(): MultiplayerState {
    return this.currentState;
  }

  public isInSession(): boolean {
    return this.gameSession.isInSession();
  }

  public isCreator(): boolean {
    return this.gameSession.isCreator();
  }

  public getSessionSummary(): string {
    return this.gameSession.getSessionSummary();
  }

  public getLastError(): string | null {
    return this.lastError;
  }

  public getSessionState() {
    return this.gameSession.getSessionState();
  }

  public getWordBoardSnapshot(): any {
    return this.gameSession.getSessionState().gameState?.wordBoardSnapshot;
  }

  public getWordBoardSnapshotVersion(): number {
    const snapshot = this.getWordBoardSnapshot();
    const boardRevision = snapshot?.boardRevision;
    return typeof boardRevision === 'number' && Number.isFinite(boardRevision) ? boardRevision : -1;
  }

  public beginBoardEntrySyncGuard() {
    return this.gameSession.beginBoardEntrySyncGuard();
  }

  public getBoardSyncGuardState() {
    return this.gameSession.getBoardSyncGuardState();
  }

  public isBoardInteractionBlocked(): boolean {
    return this.gameSession.isBoardInteractionBlocked();
  }

  public on(event: string, callback: Function) {
    return this.networkSync.on(event as any, callback as any);
  }

  public getNetworkSync(): NetworkSync {
    return this.networkSync;
  }

  // ==================== Configuration Updates ====================

  public setPlayerName(name: string): void {
    this.defaultPlayerName = name;
    if (this.shareCodeInput) {
      this.shareCodeInput.setDisplayName(name);
    }
  }

  public updateOptions(options: Partial<MultiplayerManagerOptions>): void {
    this.options = { ...this.options, ...options };
    
    if (options.playerName) {
      this.setPlayerName(options.playerName);
    }
  }

  // ==================== Debugging & Monitoring ====================

  public getDebugInfo() {
    return {
      state: this.currentState,
      isInSession: this.isInSession(),
      lastError: this.lastError,
      sessionSummary: this.getSessionSummary(),
      gameSessionDebug: this.gameSession.getDebugInfo(),
      networkStats: this.networkSync.getStats(),
      uiComponents: {
        shareCodeInput: this.shareCodeInput ? 'initialized' : 'null',
        gameLobby: this.gameLobby ? 'initialized' : 'null'
      }
    };
  }

  public getStats() {
    return {
      connectionStats: this.networkSync.getStats(),
      sessionState: this.gameSession.getSessionState(),
      managerState: this.currentState,
      isInSession: this.isInSession()
    };
  }

  // ==================== Complete Flow Orchestration ====================

  public async quickJoinFlow(shareCode: string, playerName?: string): Promise<void> {
    const displayName = playerName || this.defaultPlayerName;
    
    try {
      await this.joinSession({
        shareCode: shareCode.toUpperCase(),
        displayName
      });
    } catch (error) {
      throw error;
    }
  }

  public async quickCreateFlow(playerName?: string, maxPlayers?: number): Promise<string> {
    const displayName = playerName || this.defaultPlayerName;
    
    try {
      await this.createSession({
        displayName,
        maxPlayers: maxPlayers || 4
      });
      
      // Wait for session creation to complete and return share code
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Session creation timeout'));
        }, 10000);
        
        const checkSessionCode = () => {
          const shareCode = this.gameSession.getShareCode();
          if (shareCode) {
            clearTimeout(timeout);
            resolve(shareCode);
          } else {
            setTimeout(checkSessionCode, 100);
          }
        };
        
        checkSessionCode();
      });
      
    } catch (error) {
      throw error;
    }
  }

  // ==================== Game Actions ====================

  public sendGameAction(actionType: string, actionData: any): void {
    console.log(`[MultiplayerManager] Sending game action: ${actionType}`);
    
    if (!this.networkSync.isConnected()) {
      console.warn('[MultiplayerManager] Cannot send game action - not connected');
      return;
    }
    
    this.networkSync.sendGameAction({
      actionType,
      actionData,
      timestamp: new Date().toISOString(),
      playerId: this.networkSync.getConnectionId() || 'unknown'
    });
  }

  public endTurn(payload?: EndTurnPayload): void {
    console.log('[MultiplayerManager] Ending turn');
    
    if (!this.networkSync.isConnected()) {
      console.warn('[MultiplayerManager] Cannot end turn - not connected');
      return;
    }

    const now = Date.now();
    if (now - this.lastEndTurnSentAt < this.END_TURN_DEBOUNCE_MS) {
      console.warn('[MultiplayerManager] Ignoring duplicate endTurn call within debounce window');
      return;
    }

    this.lastEndTurnSentAt = now;
    
    this.networkSync.endTurn(payload);
  }

  public onGameAction?: (actionType: string, data: any) => void;
  public onGameStateUpdate?: (payload: any) => void;
  public onTurnChange?: (payload: any) => void;
  public onRestoreFailed?: (message: string) => void;

  // ==================== Cleanup ======================================

  public cleanup(): void {
    console.log('[MultiplayerManager] Cleaning up');
    
    // Leave any active session
    if (this.isInSession()) {
      this.leaveSession();
    }
    
    // Clean up UI components
    if (this.shareCodeInput) {
      this.shareCodeInput.destroy();
      this.shareCodeInput = null;
    }
    
    if (this.gameLobby) {
      this.gameLobby.destroy();
      this.gameLobby = null;
    }
    
    // Clean up services
    this.gameSession.cleanup();
    this.managerSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.managerSubscriptions = [];
    // Note: NetworkSync is a singleton, don't clean it up here
    
    this.setState('disconnected');
    
    console.log('[MultiplayerManager] Cleanup completed');
  }
}